import { WorkspaceUsage } from "../src/state/types";
import { Plan } from "./plans/types";

export const PremiumPlans = [Plan.UbpCore]; // Paid, user-facing plans
export const PubliclyAvailablePlans = [Plan.Public, Plan.UbpCore]; // Plans that are available to all users

// Available features (in the `usage.features` namespace by default)
export enum Feature {
    PrivateProjects = "privateProjects",
    ForumSupport = "forumSupport",
    EmailSupport = "emailSupport",
    ChatSupport = "chatSupport",
    OnboardingCalls = "onboardingCalls",
    AdvancedAugmentations = "advancedAugmentations",
    AdvancedPreprocessing = "advancedPreprocessing",
    AugmentationVariants = "augmentationVariants",
    DedicatedDeployments = "dedicatedDeployments",
    ProductionDedicatedDeployments = "productionDedicatedDeployments",
    AutoscalingDedicatedDeployments = "autoscalingDedicatedDeployments",
    ModelMonitoring = "modelMonitoring",
    ModelEvaluation = "modelEvaluation",
    LargerModelSizes = "largerModelSizes",
    AnnotationHistory = "annotationHistory",
    OutsourcedLabeling = "outsourcedLabeling",
    RoleBasedAccessControl = "roleBasedAccessControl",
    AnnotationReview = "annotationReview",
    FolderPermissions = "folderPermissions",
    BucketMirroring = "bucketMirroring",
    WorkflowVersions = "workflowVersions",
    AccessLogs = "accessLogs",
    UnrestrictedEmailProxy = "unrestrictedEmailProxy",
    AdvancedApiKeys = "advancedApiKeys",
    CanDisablePublicProjects = "canDisablePublicProjects",
    ContinuousImprovement = "continuousImprovement",
    NeuralArchitectureSearch = "neuralArchitectureSearch",
    FolderBilling = "folderBilling",
    VisionEvents = "visionEvents",
    AuditLogs = "auditLogs",
    Solutions = "solutions",
    AiSdkWorkflowAssistant = "aiSdkWorkflowAssistant"
}

// Training-specific features (in the `usage.train` namespace)
// These features are checked via usage.train.<featureName> instead of usage.features.<featureName>
export enum TrainFeature {
    NeuralArchitectureSearch = "canTrainNas"
}

// Feature information mapping
type FeatureInformation = {
    readableName: string;
    docsUrl: string | null; // Link to docs for the feature
    description: string; // Short description of the feature
    availability: boolean | Record<Plan, boolean>; // Available for all plans, or available for specific plans
    limits?: Record<Plan, number>; // Limits for each plan (optional)
    usageObjectOverrideKeys: {
        // Keys within the `usage` object that can be used to override the feature availability or limits for a given workspace/user
        // Always starting within the `usage` object, not a specific property (ex: `features.allowPrivateProjects`)
        // If null, no override is supported
        availability: string | null; // usually `feature.X`
        limits: string | null; // usually `limit.X`
    };
};

export const featureInformation: Record<Feature, FeatureInformation> = {
    [Feature.PrivateProjects]: {
        readableName: "Private Projects",
        docsUrl: null,
        description: "Keep your projects private and restricted to only members of your workspace.",
        availability: {
            [Plan.Public]: false,
            [Plan.UbpBasic]: true,
            [Plan.UbpCore]: true,
            [Plan.UbpGrowth]: true,
            [Plan.UbpPremiumTrial]: true,
            [Plan.EnterpriseGrowth]: true,
            [Plan.Enterprise]: true,
            [Plan.Legacy]: true,
            [Plan.LegacyStarter]: true,
            [Plan.LegacySandbox]: true,
            [Plan.Research]: false,
            [Plan.UninitalizedWorkspace]: false
        },
        usageObjectOverrideKeys: { availability: "features.allowPrivateProjects", limits: null }
    },
    [Feature.ForumSupport]: {
        readableName: "Forum Support",
        docsUrl: null,
        description: "Access community forum support.",
        availability: {
            [Plan.Public]: true,
            [Plan.UbpBasic]: true,
            [Plan.UbpCore]: true,
            [Plan.UbpGrowth]: true,
            [Plan.UbpPremiumTrial]: true,
            [Plan.EnterpriseGrowth]: true,
            [Plan.Enterprise]: true,
            [Plan.Legacy]: true,
            [Plan.LegacySandbox]: true,
            [Plan.LegacyStarter]: true,
            [Plan.Research]: true,
            [Plan.UninitalizedWorkspace]: true
        },
        usageObjectOverrideKeys: { availability: null, limits: null }
    },
    [Feature.EmailSupport]: {
        readableName: "Email Support",
        docsUrl: null,
        description: "Get product support via email from our team.",
        availability: {
            [Plan.Public]: false,
            [Plan.UbpBasic]: false,
            [Plan.UbpCore]: false,
            [Plan.UbpGrowth]: true,
            [Plan.UbpPremiumTrial]: true,
            [Plan.EnterpriseGrowth]: true,
            [Plan.Enterprise]: true,
            [Plan.Legacy]: false,
            [Plan.LegacyStarter]: true,
            [Plan.LegacySandbox]: false,
            [Plan.Research]: false,
            [Plan.UninitalizedWorkspace]: false
        },
        usageObjectOverrideKeys: { availability: null, limits: null }
    },
    [Feature.ChatSupport]: {
        readableName: "Chat Support",
        docsUrl: null,
        description: "Use our in-app chat to get product support from our team.",
        availability: {
            [Plan.Public]: false,
            [Plan.UbpBasic]: false,
            [Plan.UbpCore]: false,
            [Plan.UbpGrowth]: true,
            [Plan.UbpPremiumTrial]: true,
            [Plan.EnterpriseGrowth]: true,
            [Plan.Enterprise]: true,
            [Plan.Legacy]: false,
            [Plan.LegacyStarter]: true,
            [Plan.LegacySandbox]: false,
            [Plan.Research]: false,
            [Plan.UninitalizedWorkspace]: false
        },
        usageObjectOverrideKeys: { availability: null, limits: null }
    },
    [Feature.OnboardingCalls]: {
        readableName: "Onboarding Calls",
        docsUrl: null,
        description:
            "Schedule onboarding calls with our team to get you started and answer any questions.",
        availability: {
            [Plan.Public]: false,
            [Plan.UbpBasic]: false,
            [Plan.UbpCore]: false,
            [Plan.UbpGrowth]: true,
            [Plan.UbpPremiumTrial]: false,
            [Plan.EnterpriseGrowth]: true,
            [Plan.Enterprise]: true,
            [Plan.Legacy]: false,
            [Plan.LegacyStarter]: false,
            [Plan.LegacySandbox]: false,
            [Plan.Research]: false,
            [Plan.UninitalizedWorkspace]: false
        },
        usageObjectOverrideKeys: { availability: null, limits: null }
    },
    [Feature.AdvancedAugmentations]: {
        readableName: "Advanced Augmentations",
        docsUrl: null,
        description:
            "Unlock advanced data augmentation steps to improve model performance and generalizability.",
        availability: {
            [Plan.Public]: false,
            [Plan.UbpBasic]: true,
            [Plan.UbpCore]: true,
            [Plan.UbpGrowth]: true,
            [Plan.UbpPremiumTrial]: true,
            [Plan.EnterpriseGrowth]: true,
            [Plan.Enterprise]: true,
            [Plan.Legacy]: true,
            [Plan.LegacyStarter]: true,
            [Plan.LegacySandbox]: true,
            [Plan.Research]: true,
            [Plan.UninitalizedWorkspace]: false
        },
        usageObjectOverrideKeys: { availability: "features.advancedTransforms", limits: null }
    },
    [Feature.AdvancedPreprocessing]: {
        readableName: "Advanced Preprocessing",
        docsUrl: null,
        description:
            "Unlock advanced data preprocessing steps to improve model performance and generalizability.",
        availability: {
            [Plan.Public]: false,
            [Plan.UbpBasic]: true,
            [Plan.UbpCore]: true,
            [Plan.UbpGrowth]: true,
            [Plan.UbpPremiumTrial]: true,
            [Plan.EnterpriseGrowth]: true,
            [Plan.Enterprise]: true,
            [Plan.Legacy]: true,
            [Plan.LegacyStarter]: true,
            [Plan.LegacySandbox]: true,
            [Plan.Research]: true,
            [Plan.UninitalizedWorkspace]: false
        },
        usageObjectOverrideKeys: { availability: "features.advancedTransforms", limits: null }
    },
    [Feature.AugmentationVariants]: {
        readableName: "Augmentation Variants",
        docsUrl: null,
        description:
            "Increase variants per image with augmentation to improve model performance and generalizability.",
        availability: true,
        limits: {
            [Plan.Public]: 3,
            [Plan.UbpBasic]: 5,
            [Plan.UbpCore]: 5,
            [Plan.UbpGrowth]: 10,
            [Plan.UbpPremiumTrial]: 10,
            [Plan.EnterpriseGrowth]: 15,
            [Plan.Enterprise]: 15,
            [Plan.Legacy]: 15,
            [Plan.LegacyStarter]: 10,
            [Plan.LegacySandbox]: 10,
            [Plan.Research]: 5,
            [Plan.UninitalizedWorkspace]: 3
        },
        usageObjectOverrideKeys: { availability: null, limits: "limit.variantsPerImage" }
    },
    [Feature.DedicatedDeployments]: {
        readableName: "Dedicated Deployments",
        docsUrl: "https://docs.roboflow.com/deploy/dedicated-deployments",
        description:
            "Get dedicated instances of our inference server for inferring on your custom models.",
        availability: {
            [Plan.Public]: false,
            [Plan.UbpBasic]: true,
            [Plan.UbpCore]: true,
            [Plan.UbpGrowth]: true,
            [Plan.UbpPremiumTrial]: true,
            [Plan.EnterpriseGrowth]: true,
            [Plan.Enterprise]: true,
            [Plan.Legacy]: true,
            [Plan.LegacyStarter]: true,
            [Plan.LegacySandbox]: true,
            [Plan.Research]: true,
            [Plan.UninitalizedWorkspace]: false
        },
        usageObjectOverrideKeys: { availability: null, limits: "limit.dedicatedDeployments" }
    },
    [Feature.ProductionDedicatedDeployments]: {
        readableName: "Production Dedicated Deployments",
        docsUrl: "https://docs.roboflow.com/deploy/dedicated-deployments",
        description: "Access production-grade dedicated deployment machines (non-dev instances).",
        availability: {
            [Plan.Public]: false,
            [Plan.UbpBasic]: true,
            [Plan.UbpCore]: true,
            [Plan.UbpGrowth]: true,
            [Plan.UbpPremiumTrial]: true,
            [Plan.EnterpriseGrowth]: true,
            [Plan.Enterprise]: true,
            [Plan.Legacy]: true,
            [Plan.LegacyStarter]: false,
            [Plan.LegacySandbox]: true,
            [Plan.Research]: true,
            [Plan.UninitalizedWorkspace]: false
        },
        usageObjectOverrideKeys: { availability: null, limits: null }
    },
    [Feature.AutoscalingDedicatedDeployments]: {
        readableName: "Autoscaling Dedicated Deployments",
        docsUrl: "https://docs.roboflow.com/deploy/dedicated-deployments",
        description:
            "Enable autoscaling for dedicated deployments to automatically adjust replicas based on load.",
        availability: {
            [Plan.Public]: false,
            [Plan.UbpBasic]: false,
            [Plan.UbpCore]: false,
            [Plan.UbpGrowth]: true,
            [Plan.UbpPremiumTrial]: true,
            [Plan.EnterpriseGrowth]: true,
            [Plan.Enterprise]: true,
            [Plan.Legacy]: true,
            [Plan.LegacyStarter]: false,
            [Plan.LegacySandbox]: false,
            [Plan.Research]: false,
            [Plan.UninitalizedWorkspace]: false
        },
        usageObjectOverrideKeys: { availability: null, limits: null }
    },
    [Feature.ModelMonitoring]: {
        readableName: "Model Monitoring",
        docsUrl: "https://docs.roboflow.com/deploy/model-monitoring",
        description: "Monitor model performance in real time.",
        availability: {
            [Plan.Public]: false,
            [Plan.UbpBasic]: false,
            [Plan.UbpCore]: false,
            [Plan.UbpGrowth]: true,
            [Plan.UbpPremiumTrial]: true,
            [Plan.EnterpriseGrowth]: true,
            [Plan.Enterprise]: true,
            [Plan.Legacy]: false,
            [Plan.LegacyStarter]: false,
            [Plan.LegacySandbox]: false,
            [Plan.Research]: false,
            [Plan.UninitalizedWorkspace]: false
        },
        usageObjectOverrideKeys: { availability: "features.modelMonitoring", limits: null }
    },
    [Feature.ModelEvaluation]: {
        readableName: "Model Evaluation",
        docsUrl: "https://docs.roboflow.com/train/evaluate-trained-models",
        description:
            "Evaluate models to identify improvements, find annotation discrepancies, and more.",
        availability: {
            [Plan.Public]: false,
            [Plan.UbpBasic]: true,
            [Plan.UbpCore]: true,
            [Plan.UbpGrowth]: true,
            [Plan.UbpPremiumTrial]: true,
            [Plan.EnterpriseGrowth]: true,
            [Plan.Enterprise]: true,
            [Plan.Legacy]: true,
            [Plan.LegacyStarter]: true,
            [Plan.LegacySandbox]: true,
            [Plan.Research]: true,
            [Plan.UninitalizedWorkspace]: false
        },
        usageObjectOverrideKeys: { availability: "features.modelEvaluation", limits: null }
    },
    [Feature.LargerModelSizes]: {
        readableName: "Larger Model Sizes",
        docsUrl: null,
        description: "Train models with larger model sizes to achieve even better performance.",
        availability: {
            [Plan.Public]: false,
            [Plan.UbpBasic]: true,
            [Plan.UbpCore]: true,
            [Plan.UbpGrowth]: true,
            [Plan.UbpPremiumTrial]: true,
            [Plan.EnterpriseGrowth]: true,
            [Plan.Enterprise]: true,
            [Plan.Legacy]: true,
            [Plan.LegacyStarter]: true,
            [Plan.LegacySandbox]: true,
            [Plan.Research]: true,
            [Plan.UninitalizedWorkspace]: false
        },
        usageObjectOverrideKeys: { availability: null, limits: null }
    },
    [Feature.AnnotationHistory]: {
        readableName: "Annotation History",
        docsUrl: "https://docs.roboflow.com/annotate/use-roboflow-annotate/annotation-history",
        description: "Trace the history of annotations made for an image.",
        availability: {
            [Plan.Public]: false,
            [Plan.UbpBasic]: false,
            [Plan.UbpCore]: false,
            [Plan.UbpGrowth]: true,
            [Plan.UbpPremiumTrial]: true,
            [Plan.EnterpriseGrowth]: true,
            [Plan.Enterprise]: true,
            [Plan.Legacy]: true,
            [Plan.LegacyStarter]: true,
            [Plan.LegacySandbox]: false,
            [Plan.Research]: false,
            [Plan.UninitalizedWorkspace]: false
        },
        usageObjectOverrideKeys: { availability: null, limits: null }
    },
    [Feature.OutsourcedLabeling]: {
        readableName: "Outsourced Labeling",
        docsUrl: "https://roboflow.com/roboflow-labeling",
        description:
            "Work with a professional team of human labelers to get your images labeled faster.",
        availability: {
            [Plan.Public]: false,
            [Plan.UbpBasic]: false,
            [Plan.UbpCore]: false,
            [Plan.UbpGrowth]: true,
            [Plan.UbpPremiumTrial]: false,
            [Plan.EnterpriseGrowth]: true,
            [Plan.Enterprise]: true,
            [Plan.Legacy]: true,
            [Plan.LegacyStarter]: true,
            [Plan.LegacySandbox]: false,
            [Plan.Research]: false,
            [Plan.UninitalizedWorkspace]: false
        },
        usageObjectOverrideKeys: { availability: null, limits: null }
    },
    [Feature.RoleBasedAccessControl]: {
        readableName: "Role-Based Access Control",
        docsUrl: "https://docs.roboflow.com/team-members/role-based-access-control",
        description: "Manage fine-grained permissions for workspace members using roles.",
        availability: {
            [Plan.Public]: false,
            [Plan.UbpBasic]: false,
            [Plan.UbpCore]: false,
            [Plan.UbpGrowth]: true,
            [Plan.UbpPremiumTrial]: true,
            [Plan.EnterpriseGrowth]: true,
            [Plan.Enterprise]: true,
            [Plan.Legacy]: true,
            [Plan.LegacyStarter]: true,
            [Plan.LegacySandbox]: true,
            [Plan.Research]: false
        },
        usageObjectOverrideKeys: { availability: "features.rbac", limits: null }
    },
    [Feature.AnnotationReview]: {
        readableName: "Annotation Review",
        docsUrl: "https://docs.roboflow.com/annotate/team-collaboration#review-mode",
        description: "Review annotations made for an image.",
        availability: {
            [Plan.Public]: false,
            [Plan.UbpBasic]: false,
            [Plan.UbpCore]: false,
            [Plan.UbpGrowth]: true,
            [Plan.UbpPremiumTrial]: true,
            [Plan.EnterpriseGrowth]: true,
            [Plan.Enterprise]: true,
            [Plan.Legacy]: true,
            [Plan.LegacyStarter]: false,
            [Plan.LegacySandbox]: false,
            [Plan.Research]: false,
            [Plan.UninitalizedWorkspace]: false
        },
        usageObjectOverrideKeys: { availability: "features.annotationReview", limits: null }
    },
    [Feature.FolderPermissions]: {
        readableName: "Folder Permissions",
        docsUrl: null,
        description: "Restrict project folders to specific roles.",
        availability: {
            [Plan.Public]: false,
            [Plan.UbpBasic]: false,
            [Plan.UbpCore]: false,
            [Plan.UbpGrowth]: false,
            [Plan.UbpPremiumTrial]: false,
            [Plan.EnterpriseGrowth]: false,
            [Plan.Enterprise]: false, // Add-on: Enterprise Access Control & Data Governance
            [Plan.Legacy]: false,
            [Plan.LegacyStarter]: false,
            [Plan.LegacySandbox]: false,
            [Plan.Research]: false,
            [Plan.UninitalizedWorkspace]: false
        },
        usageObjectOverrideKeys: { availability: "features.folderPermissions", limits: null }
    },
    [Feature.BucketMirroring]: {
        readableName: "Bucket Mirroring",
        docsUrl: null,
        description:
            "Enable cloud bucket mirroring functionality where we mirror files from a S3 cloud bucket.",
        availability: {
            [Plan.Public]: false,
            [Plan.UbpBasic]: false,
            [Plan.UbpCore]: false,
            [Plan.UbpGrowth]: false,
            [Plan.UbpPremiumTrial]: false,
            [Plan.EnterpriseGrowth]: false,
            [Plan.Enterprise]: false, // only for CGIAR/Gates for now
            [Plan.Legacy]: false,
            [Plan.LegacyStarter]: false,
            [Plan.LegacySandbox]: false,
            [Plan.Research]: false,
            [Plan.UninitalizedWorkspace]: false
        },
        usageObjectOverrideKeys: { availability: "features.bucketMirroring", limits: null }
    },
    [Feature.WorkflowVersions]: {
        readableName: "Workflow Versions",
        docsUrl: "https://docs.roboflow.com/workflows/manage-workflow-versions",
        description: "Save and restore versions of your workflows.",
        availability: {
            [Plan.Public]: false,
            [Plan.UbpBasic]: false,
            [Plan.UbpCore]: false,
            [Plan.UbpGrowth]: true,
            [Plan.UbpPremiumTrial]: true,
            [Plan.EnterpriseGrowth]: true,
            [Plan.Enterprise]: true,
            [Plan.Legacy]: false,
            [Plan.LegacyStarter]: false,
            [Plan.LegacySandbox]: false,
            [Plan.Research]: false,
            [Plan.UninitalizedWorkspace]: false
        },
        usageObjectOverrideKeys: { availability: null, limits: null }
    },
    [Feature.AccessLogs]: {
        readableName: "Access Logs",
        docsUrl: null,
        description: "View access logs for workspace activities and user actions.",
        availability: {
            [Plan.Public]: false,
            [Plan.UbpBasic]: false,
            [Plan.UbpCore]: false,
            [Plan.UbpGrowth]: false,
            [Plan.UbpPremiumTrial]: false,
            [Plan.EnterpriseGrowth]: false,
            [Plan.Enterprise]: false,
            [Plan.Legacy]: false,
            [Plan.LegacyStarter]: false,
            [Plan.LegacySandbox]: false,
            [Plan.Research]: false,
            [Plan.UninitalizedWorkspace]: false
        },
        usageObjectOverrideKeys: { availability: "features.accessLogs", limits: null }
    },
    [Feature.UnrestrictedEmailProxy]: {
        readableName: "Unrestricted Email Proxy",
        docsUrl: null,
        description:
            "Allow sending emails to non-workspace members via the email proxy (for trusted enterprise customers).",
        availability: {
            [Plan.Public]: false,
            [Plan.UbpBasic]: false,
            [Plan.UbpCore]: false,
            [Plan.UbpGrowth]: false,
            [Plan.UbpPremiumTrial]: false,
            [Plan.EnterpriseGrowth]: false,
            [Plan.Enterprise]: false,
            [Plan.Legacy]: false,
            [Plan.LegacyStarter]: false,
            [Plan.LegacySandbox]: false,
            [Plan.Research]: false,
            [Plan.UninitalizedWorkspace]: false
        },
        usageObjectOverrideKeys: { availability: "features.unrestrictedEmailProxy", limits: null }
    },
    [Feature.AdvancedApiKeys]: {
        readableName: "Advanced API Keys",
        docsUrl: null,
        description:
            "Advanced API key management including scoped permissions, multiple keys, and key disabling.",
        availability: {
            [Plan.Public]: false,
            [Plan.UbpBasic]: false,
            [Plan.UbpCore]: false,
            [Plan.UbpGrowth]: false,
            [Plan.UbpPremiumTrial]: false,
            [Plan.EnterpriseGrowth]: true,
            [Plan.Enterprise]: false, // Add-on: Enterprise Access Control & Data Governance
            [Plan.Legacy]: false,
            [Plan.LegacyStarter]: false,
            [Plan.LegacySandbox]: false,
            [Plan.Research]: false,
            [Plan.UninitalizedWorkspace]: false
        },
        usageObjectOverrideKeys: { availability: "features.advancedApiKeys", limits: null }
    },
    [Feature.CanDisablePublicProjects]: {
        readableName: "Can Disable Public Projects",
        docsUrl: null,
        description: "Allow workspaces to disable creation of public projects.",
        availability: {
            [Plan.Public]: false,
            [Plan.UbpBasic]: false,
            [Plan.UbpCore]: false,
            [Plan.UbpGrowth]: false,
            [Plan.UbpPremiumTrial]: false,
            [Plan.EnterpriseGrowth]: true,
            [Plan.Enterprise]: true,
            [Plan.Legacy]: false,
            [Plan.LegacyStarter]: false,
            [Plan.LegacySandbox]: false,
            [Plan.Research]: false,
            [Plan.UninitalizedWorkspace]: false
        },
        usageObjectOverrideKeys: { availability: "features.canDisablePublicProjects", limits: null }
    },
    [Feature.ContinuousImprovement]: {
        readableName: "Continuous Improvement",
        docsUrl: null,
        description: "Automatically trigger model retraining when dataset thresholds are crossed.",
        availability: {
            [Plan.Public]: false,
            [Plan.UbpBasic]: false,
            [Plan.UbpCore]: false,
            [Plan.UbpGrowth]: false,
            [Plan.UbpPremiumTrial]: false,
            [Plan.EnterpriseGrowth]: false,
            [Plan.Enterprise]: false,
            [Plan.Legacy]: false,
            [Plan.LegacyStarter]: false,
            [Plan.LegacySandbox]: false,
            [Plan.Research]: false,
            [Plan.UninitalizedWorkspace]: false
        },
        usageObjectOverrideKeys: { availability: "features.continuousImprovement", limits: null }
    },
    [Feature.NeuralArchitectureSearch]: {
        readableName: "Neural Architecture Search",
        docsUrl: null,
        description:
            "Automatically find the best model architecture for your dataset using neural architecture search.",
        availability: {
            [Plan.Public]: false,
            [Plan.UbpBasic]: false,
            [Plan.UbpCore]: false,
            [Plan.UbpGrowth]: false,
            [Plan.UbpPremiumTrial]: false,
            [Plan.EnterpriseGrowth]: false,
            [Plan.Enterprise]: false,
            [Plan.Legacy]: false,
            [Plan.LegacyStarter]: false,
            [Plan.LegacySandbox]: false,
            [Plan.Research]: false,
            [Plan.UninitalizedWorkspace]: false
        },
        usageObjectOverrideKeys: { availability: "features.neuralArchitectureSearch", limits: null }
    },
    [Feature.FolderBilling]: {
        readableName: "Billing Folders",
        docsUrl: null,
        description: "Attribute usage and costs to billing folders for granular reporting.",
        availability: {
            [Plan.Public]: false,
            [Plan.UbpBasic]: false,
            [Plan.UbpCore]: false,
            [Plan.UbpGrowth]: false,
            [Plan.UbpPremiumTrial]: false,
            [Plan.EnterpriseGrowth]: false,
            [Plan.Enterprise]: false,
            [Plan.Legacy]: false,
            [Plan.LegacyStarter]: false,
            [Plan.LegacySandbox]: false,
            [Plan.Research]: false,
            [Plan.UninitalizedWorkspace]: false
        },
        usageObjectOverrideKeys: { availability: "features.folderBilling", limits: null }
    },
    [Feature.VisionEvents]: {
        readableName: "Vision Events",
        docsUrl: null,
        description:
            "Ingest, query, and visualize vision events from deployed workflows and devices.",
        availability: {
            [Plan.Public]: false,
            [Plan.UbpBasic]: false,
            [Plan.UbpCore]: false,
            [Plan.UbpGrowth]: false,
            [Plan.UbpPremiumTrial]: false,
            [Plan.EnterpriseGrowth]: false,
            [Plan.Enterprise]: false,
            [Plan.Legacy]: false,
            [Plan.LegacyStarter]: false,
            [Plan.LegacySandbox]: false,
            [Plan.Research]: false,
            [Plan.UninitalizedWorkspace]: false
        },
        usageObjectOverrideKeys: { availability: "features.visionEvents", limits: null }
    },
    [Feature.AuditLogs]: {
        readableName: "Audit Logs",
        docsUrl: null,
        description:
            "View audit logs tracking who did what and when in the workspace for compliance and accountability.",
        availability: {
            [Plan.Public]: false,
            [Plan.UbpBasic]: false,
            [Plan.UbpCore]: false,
            [Plan.UbpGrowth]: false,
            [Plan.UbpPremiumTrial]: false,
            [Plan.EnterpriseGrowth]: false,
            [Plan.Enterprise]: false,
            [Plan.Legacy]: false,
            [Plan.LegacyStarter]: false,
            [Plan.LegacySandbox]: false,
            [Plan.Research]: false,
            [Plan.UninitalizedWorkspace]: false
        },
        usageObjectOverrideKeys: { availability: "features.auditLogs", limits: null }
    },
    [Feature.Solutions]: {
        readableName: "Solutions",
        docsUrl: null,
        description: "AI-powered solution builder for creating complete computer vision solutions.",
        availability: {
            [Plan.Public]: false,
            [Plan.UbpBasic]: false,
            [Plan.UbpCore]: false,
            [Plan.UbpGrowth]: false,
            [Plan.UbpPremiumTrial]: false,
            [Plan.EnterpriseGrowth]: false,
            [Plan.Enterprise]: false,
            [Plan.Legacy]: false,
            [Plan.LegacyStarter]: false,
            [Plan.LegacySandbox]: false,
            [Plan.Research]: false,
            [Plan.UninitalizedWorkspace]: false
        },
        usageObjectOverrideKeys: { availability: "features.solutions", limits: null }
    },
    [Feature.AiSdkWorkflowAssistant]: {
        readableName: "AI SDK Workflow Assistant",
        docsUrl: null,
        description: "New Vercel AI SDK-based workflow assistant (V2).",
        availability: {
            [Plan.Public]: false,
            [Plan.UbpBasic]: false,
            [Plan.UbpCore]: false,
            [Plan.UbpGrowth]: false,
            [Plan.UbpPremiumTrial]: false,
            [Plan.EnterpriseGrowth]: false,
            [Plan.Enterprise]: false,
            [Plan.Legacy]: false,
            [Plan.LegacyStarter]: false,
            [Plan.LegacySandbox]: false,
            [Plan.Research]: false,
            [Plan.UninitalizedWorkspace]: false
        },
        usageObjectOverrideKeys: { availability: "features.aiSdkWorkflowAssistant", limits: null }
    }
};

/**
 * Checks if a feature is available for a given plan. Only use to check features provided on a plan, use isFeatureAvailable for gating features for users since it checks for feature flags on the user's usage object.
 * @param feature - The feature to check
 * @param plan - The plan to check
 * @returns true if the feature is available, false otherwise
 */
export function isFeatureAvailableOnPlan(feature: Feature, plan: Plan): boolean {
    const featureInfo = featureInformation[feature];
    if (!featureInfo) {
        return false;
    }

    const availability = featureInfo.availability;
    if (typeof availability === "boolean") {
        return availability;
    }

    return availability[plan] ?? false;
}

/**
 * Gets the limit for a feature for a given plan.
 * @param feature - The feature to check
 * @param plan - The plan to check
 * @returns the limit for the feature, or null if no limit
 */
export function getFeatureLimitOnPlan(feature: Feature, plan: Plan): number | null {
    const featureInfo = featureInformation[feature];
    if (!featureInfo) {
        return null;
    }

    const limits = featureInfo.limits;
    if (typeof limits === "number") {
        return limits;
    }
    return limits?.[plan] ?? null;
}

function getValueFromUsageObject(usage: object, path: string): any {
    const keys = path.split(".");
    let currentValue: any = usage;
    for (const key of keys) {
        if (currentValue === undefined || currentValue === null) {
            return undefined;
        }
        currentValue = currentValue[key];
    }
    return currentValue;
}

/**
 * Checks if a feature is available for a given user's usage object.
 * @param feature - The feature to check
 * @param usage - The usage information. Pass directly from the usage zustand store (preferred) or the `window.usage` object. Never construct this object yourself.
 * @returns true if the feature is available, false otherwise
 */
export function isFeatureAvailable(feature: Feature, usage: WorkspaceUsage): boolean {
    if (!isValidFeatureId(feature)) {
        throw new Error(`Invalid feature: ${feature}`);
    }

    const featureInfo = featureInformation[feature];
    const overrideKey = featureInfo?.usageObjectOverrideKeys?.availability;

    // Check for manual override in usage object
    if (overrideKey) {
        const overrideValue = getValueFromUsageObject(usage, overrideKey);
        if (overrideValue === true) {
            // So far, there isn't a need to override and disable features via override flag
            // but if the key exists and is true, it should override
            return true;
        }
    }

    // Fall back to plan-based availability
    return isFeatureAvailableOnPlan(feature, usage?.plan as Plan);
}

/**
 * Gets the limit for a feature for a given user's usage object.
 * @param feature - The feature to check
 * @param usage - The usage information
 * @returns the limit for the feature, or null if no limit
 */
export function getFeatureLimit(feature: Feature, usage: WorkspaceUsage): number | null {
    const featureInfo = featureInformation[feature];
    const overrideKey = featureInfo?.usageObjectOverrideKeys?.limits;

    // Check for manual override in usage object
    if (overrideKey) {
        const overrideValue = getValueFromUsageObject(usage, overrideKey);
        if (typeof overrideValue === "number") {
            return overrideValue;
        }
    }

    // Fall back to plan-based limits
    const limits = featureInfo?.limits;
    if (typeof limits === "number") {
        return limits;
    }

    return getFeatureLimitOnPlan(feature, usage?.plan as Plan);
}

/**
 * Checks if a feature is a "Trial Feature": Available in the trial plan but not in the free Public plan.
 * @param feature - The feature to check
 * @returns true if the feature is available in the trial plan, false otherwise
 */
export function isTrialFeature(feature: Feature): boolean {
    const trialAvailable = isFeatureAvailableOnPlan(feature, Plan.UbpPremiumTrial);
    const publicAvailable = isFeatureAvailableOnPlan(feature, Plan.Public);

    return trialAvailable && !publicAvailable;
}

/**
 * Checks if a feature is a "Premium Feature": Available in any user-facing paid plan.
 * @param feature - The feature to check
 * @returns true if the feature is available in any paid plan, false otherwise
 */
export function isPremiumFeature(feature: Feature): boolean {
    return PremiumPlans.some((plan) => isFeatureAvailableOnPlan(feature, plan));
}

export function getPlansFeatureAvailableOn(feature: Feature): Plan[] {
    const info = featureInformation[feature];
    if (!info) {
        return [];
    }

    const availability = info.availability;
    if (typeof availability === "boolean") {
        if (!availability) {
            return [];
        }
        return (Object.values(Plan) as Array<Plan | null>).filter(
            (plan): plan is Plan => plan !== null && plan !== Plan.Legacy
        );
    }

    return Object.keys(availability).filter((plan) => availability[plan]) as Plan[];
}

export function isValidFeatureId(id: string | Feature): id is Feature {
    return Object.values(Feature).includes(id as Feature);
}

export function isValidTrainFeatureId(id: string | TrainFeature): id is TrainFeature {
    return Object.values(TrainFeature).includes(id as TrainFeature);
}

export function isUpsellableFeature(feature: Feature): boolean {
    return getPlansFeatureAvailableOn(feature).length > 0;
}

// =============================================================================
// Train Feature Information
// =============================================================================

// Train feature information mapping
type TrainFeatureInformation = {
    readableName: string;
    description: string;
    // Plans that have this feature enabled by default (without needing an override flag)
    enabledByDefaultOnPlans: Plan[];
};

export const trainFeatureInformation: Record<TrainFeature, TrainFeatureInformation> = {
    [TrainFeature.NeuralArchitectureSearch]: {
        readableName: "Neural Architecture Search",
        description:
            "Automatically find the best model architecture for your dataset using neural architecture search.",
        enabledByDefaultOnPlans: [] // No plans have NAS by default, must be enabled via usage.train.canTrainNAS
    }
};

/**
 * Checks if a train feature is available on a specific plan by default.
 * @param feature - The train feature to check
 * @param plan - The plan to check
 * @returns true if the feature is available on this plan by default
 */
export function isTrainFeatureAvailableOnPlan(feature: TrainFeature, plan: Plan): boolean {
    const featureInfo = trainFeatureInformation[feature];
    if (!featureInfo) {
        return false;
    }
    return featureInfo.enabledByDefaultOnPlans.includes(plan);
}

/**
 * Checks if a train feature is available for a given user's usage object.
 * Train features are checked in the `usage.train` namespace (e.g., usage.train.canTrainNAS).
 *
 * @param feature - The train feature to check
 * @param usage - The usage information
 * @returns true if the feature is available, false otherwise
 */
export function isTrainFeatureAvailable(feature: TrainFeature, usage: WorkspaceUsage): boolean {
    if (!isValidTrainFeatureId(feature)) {
        throw new Error(`Invalid train feature: ${feature}`);
    }

    // Check for override in usage.train namespace
    // The TrainFeature enum value is the key in usage.train (e.g., "canTrainNAS")
    const overrideValue = usage?.train?.[feature as keyof typeof usage.train];
    if (overrideValue === true) {
        return true;
    }

    // Fall back to plan-based availability
    return isTrainFeatureAvailableOnPlan(feature, usage?.plan as Plan);
}

/**
 * Checks if Neural Architecture Search is available for the given workspace usage.
 * This is a facade function for isTrainFeatureAvailable(TrainFeature.NeuralArchitectureSearch, usage).
 *
 * @param usage - The workspace usage object
 * @returns true if NAS is available, false otherwise
 */
export function isNASAvailable(usage: WorkspaceUsage): boolean {
    return isTrainFeatureAvailable(TrainFeature.NeuralArchitectureSearch, usage);
}
